<template>
  <div class="w-100">
    <v-card
      v-if="isLoading"
      class="py-4"
    >
      <u-loader />
    </v-card>
    <div v-else>
      <v-card class="mx-auto">
        <v-card-title>
          <span class="title ml-4">Überprüfung aller Eingabenkombinationen um fehlende Resultate zu
            identifizieren</span>
        </v-card-title>

        <v-card-text v-if="hasNeverBeenValidated">
          <v-alert
            :value="true"
            type="warning"
            class="mt-4"
          >
            <strong>{{ validationInfo?.message }}</strong><br>
          </v-alert>
        </v-card-text>

        <!-- Last sync info (only when not syncing and a completed sync exists) -->
        <v-card-text v-if="!hasNeverBeenValidated">
          <v-alert
            :value="true"
            type="info"
            class="mt-4"
          >
            {{ validationInfo?.message }}<br>
            <strong>Letzte Prüfung:</strong> {{ formatDate(validationInfo?.created) }}
          </v-alert>
        </v-card-text>

        <v-card-text class="text-right">
          <v-btn
            class="btn-left mt-4"
            color="success"
            raised
            :disabled="isLoading"
            title="Prüfung starten"
            @click="runCriteriaValidation"
          >
            <i
              class="fas fa-sync mr-3"
              :class="{ 'fa-spin': isLoading }"
            /> Prüfung starten
          </v-btn>
        </v-card-text>

        <v-card-text v-if="!hasNeverBeenValidated && validationInfo?.issues > 0">
          <strong>Fehlende Resultate:</strong><br>
          <v-data-table
            :headers="tableHeaders"
            :items="tableItems"
            :custom-sort="customSort"
            class="resultTable"
          >
            <template v-slot:items="props">
              <td
                v-for="(header, index) in tableHeaders"
                :key="index"
              >
                <div v-if="props.item[header.value].isLocalised">
                  {{ props.item[header.value].preview }}
                  <u-tree-view
                    :disabled="true"
                    :value="props.item[header.value].allValues"
                    :tree-view-items="locationOptions"
                    item-key="path"
                    class="pt-1"
                  />
                </div>
                <div v-else>
                  <div
                    v-if="props.item[header.value].allValues && props.item[header.value].allValues.length > 4"
                    v-b-tooltip.hover
                    :title="props.item[header.value].allValues.join(',\n')"
                    class="pt-2"
                  >
                    {{ props.item[header.value].preview }}
                  </div>
                  <div
                    v-else
                    class="pt-2"
                  >
                    {{ props.item[header.value].preview }}
                  </div>
                </div>
              </td>
            </template>
          </v-data-table>
        </v-card-text>

        <v-alert
          v-if="errorObject"
          type="error"
          class="mx-4 mb-4"
        >
          {{ errorObject }}
        </v-alert>
      </v-card>
    </div>
  </div>
</template>
<script>

import { MinimumWageMixin } from "@/components/minimum.wage/mixins/MinimumWageMixin";
import { GetCriteriaValidation, RunCriteriaValidation } from "@/services/minimum.wage.services/validation.minimum.wages.js";
import UTreeView from "@/components/common/UTreeView.vue";

export default {
  components: { UTreeView },
  mixins: [MinimumWageMixin],
  data() {
    return {
      validationInfo: null,
      tableHeaders: [],
      tableItems: [],
      errorObject: null,
      isLoading: false,
    };
  },
  computed: {
    hasNeverBeenValidated() {
      return this.validationInfo && this.validationInfo.combinationsWithoutResult === null && this.validationInfo.structuresWithoutValues === null;
    },
    locationOptions() {
      let locationsOfEdition =
        this.$store.state.mindestLohnKonfiguration.ValidEditionLocations;

      let citiesCopy = JSON.parse(
        JSON.stringify(this.$store.state.mindestLohnKonfiguration.Cities),
      );
      let topLevel = [
        {
          name: "CH",
          path: "CH",
          children: [],
          shutoff: false,
        },
      ];
      const cantonIdMap = new Map();
      for (const item of citiesCopy) {
        if (!cantonIdMap.has(item.cantonId)) {
          cantonIdMap.set(item.cantonId, true);
          if (locationsOfEdition.some((x) => x.cantonId == item.cantonId)) {
            topLevel[0].children.push({
              id: item.cantonId,
              name: `${item.canton} (${item.cantonCode})`,
              path: `CH/${item.cantonCode}`,
              children: [],
              shutoff: false,
            });
          }
        }
      }

      const districtCodeMap = new Map();
      for (const item of citiesCopy) {
        if (!districtCodeMap.has(item.districtCode)) {
          districtCodeMap.set(item.districtCode, true);
          let cantonOfDistrict = topLevel[0].children.find(
            (x) => x.path === `CH/${item.cantonCode}`,
          );
          if (
            locationsOfEdition.some(
              (x) =>
                x.cantonId == item.cantonId && x.districtId == item.districtId,
            )
          ) {
            cantonOfDistrict.children.push({
              id: item.districtId,
              name: item.district,
              districtCode: item.districtCode,
              path: `CH/${item.cantonCode}/${item.districtCode}`,
              children: [],
              shutoff: false,
            });
          }
        }
      }

      citiesCopy.map((item) => {
        let cantonOfCity = topLevel[0].children.find(
          (x) => x.path === `CH/${item.cantonCode}`,
        );
        if (cantonOfCity) {
          let districtOfCity = cantonOfCity.children.find(
            (x) => x.path === `CH/${item.cantonCode}/${item.districtCode}`,
          );
          if (
            locationsOfEdition.some(
              (x) =>
                x.cantonId == item.cantonId &&
                x.districtId == item.districtId &&
                x.cityId == item.cityId,
            )
          ) {
            item.name = `${item.plz} ${item.city}`;
            item.id = item.cityId;
            item.path = `CH/${item.cantonCode}/${item.districtCode}/${
              item.onrp
            }`;
            item.shutoff = false;
            districtOfCity.children.push(item);
          }
        }
      });

      return topLevel;
    },
  },
  watch: {
    validationInfo(newValue) {
      if (newValue && (newValue.combinationsWithoutResult || newValue.structuresWithoutValues)) {
        this.createTable();
      }
    },
  },
  mounted() {
    this.getValidationResults();
  },
  methods: {
    customSort(items, header, isDescending){
      if(!header){
        return items; //case when 'unsorted'
      }
      if(isDescending){
        items.sort((a, b) => a[header].preview > b[header].preview ? -1 : 1);
      }
      else{
        items.sort((a, b) => a[header].preview > b[header].preview ? 1 : -1);
      }
      return items;
    },
    getValidationResults() {
      this.errorObject = null;
      this.isLoading = true;

      GetCriteriaValidation(
        this.contractId,
        this.versionId,
        this.editionId,
        this.editionId,
        (result) => {
          this.validationInfo = result;
          this.createTable();
          this.isLoading = false;
        },
        (error) => {
          this.errorObject = error;
          this.isLoading = false;
          console.error(error);
        },
      );
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleString("de-CH");
    },
    runCriteriaValidation() {
      this.validationInfo = null;
      this.errorObject = null;
      this.isLoading = true;

      RunCriteriaValidation(
        this.contractId,
        this.versionId,
        this.editionId,
        this.editionId,
        (result) => {
          this.validationInfo = result;
          this.createTable();
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
          this.errorObject = error;
          console.error(error);
        },
      );
    },
    createTable() {
      const combinationsWithoutResultArray = JSON.parse(this.validationInfo?.combinationsWithoutResult);
      const structuresWithoutValues = JSON.parse(this.validationInfo?.stucturesWithoutValues);

      if(combinationsWithoutResultArray && combinationsWithoutResultArray.length > 0){
        let headers = [];
        let headerWidth = 100 / combinationsWithoutResultArray[0].length;
        combinationsWithoutResultArray[0].forEach((header) => {
          headers.push({
            text: header.Name,
            value: `variable${header.CriteriaStructureId}`,
            width: `${headerWidth}%`,
          });
        });
        this.tableHeaders = headers;

        let values = [];
        combinationsWithoutResultArray.forEach((row) => {
          let rowData = {};
          row.forEach((cell) => {
            rowData[`variable${cell.CriteriaStructureId}`] = {
              preview: cell.Preview ?? cell.Value,
              isLocalised: cell.IsLocalised,
              allValues: cell.Values
            };
          });
          values.push(rowData);
        });
        this.tableItems = values;
      }

      if(structuresWithoutValues && structuresWithoutValues.length > 0){
        let headers = [
          {
            text: "Eingabestruktur",
            value: "name",
            width: "50%"
          },
          {
            text: "Sortierung",
            value: "order",
            width: "25%"
          }
        ]

        this.tableHeaders = headers;

        let values = [];
        structuresWithoutValues.forEach((structure) => {
          values.push({
            name: structure.Name,
            order: structure.Order
          })
        });
        this.tableItems = values;
      }

      
    },
  },
};
</script>

<style scoped>
.resultTable td {
  vertical-align: top;
}

.icon--box {
  top: -30px;
  background-color: #e72b37;
  border-radius: 4px;
  text-align: start !important;
  padding: 28px !important;
  margin-bottom: -24px !important;
  font-size: 20px;
  color: #fff;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease-in-out;
  overflow: hidden;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  height: 0;
  max-height: 0;
  transform: translateY(-20px);
}

.fade-leave-to {
  transform: translateY(20px);
}

.fade-out-enter-active,
.fade-out-leave-active {
  transition: all 1s ease-in-out;
  overflow: hidden;
}

.fade-out-enter,
.fade-out-leave-to {
  opacity: 0;
  height: 0;
  max-height: 0;
  transform: translateY(20px);
}

.fade-out-leave-to {
  transform: translateY(-20px);
}

.animate-text {
  animation-name: slideInFromLeft;
  animation-duration: 1s;
}

@keyframes slideInFromLeft {
  0% {
    opacity: 0;
    transform: translateX(-20%);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>